import React, { useState, useEffect } from 'react';
import { apiRequest } from '@/utils/api-request';
import { downloadCsvExport } from '@/utils/download-csv';
import { Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, MessageCircleQuestion, CheckCircle, XCircle, MessageSquare, Edit2, Send, Filter, X, Download } from 'lucide-react';
import { showToast } from '@/components/ui/Toast';
import Button from '@/components/ui/Button';
import SettingsSlideModal from '@/components/Modal/SettingsSlideModal';
import { DateRangePicker, Range } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import moment from 'moment';

interface QuestionData {
    question_id: number;
    slate_id_fk: number;
    user_id_fk: number;
    title: string;
    content: string;
    created_at: string;
    status: number;
    slate_name: string;
    claimed: number;
    is_client: number;
    user_name: string;
    user_phone: string;
    user_email: string;
    answer_count: number;
    answers?: AnswerData[];
}

interface AnswerData {
    answer_id: number;
    content: string;
    created_at: string;
    status: number;
    user_name: string;
    user_phone: string;
    user_email: string;
}

interface Pagination {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
}

const QAAdmin: React.FC = () => {
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [pagination, setPagination] = useState<Pagination>({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 25,
    });
    const [sortColumn, setSortColumn] = useState('created_at');
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');
    const [claimedFilter, setClaimedFilter] = useState<string>('');
    const [clientFilter, setClientFilter] = useState<string>('');
    const [dateRange, setDateRange] = useState<Range[] | null>(null);
    const [dateRangeDraft, setDateRangeDraft] = useState<Range[]>([
        { startDate: new Date(), endDate: new Date(), key: 'selection' },
    ]);
    const [showDateRangePicker, setShowDateRangePicker] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    // Modal state
    const [selectedQuestion, setSelectedQuestion] = useState<QuestionData | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);

    // Edit state
    const [isEditing, setIsEditing] = useState(false);
    const [editedTitle, setEditedTitle] = useState('');
    const [editedContent, setEditedContent] = useState('');

    // Answer state
    const [newAnswer, setNewAnswer] = useState('');
    const [isSubmittingAnswer, setIsSubmittingAnswer] = useState(false);

    // Fetch questions
    const fetchQuestions = async (page: number = pagination.currentPage) => {
        try {
            setIsLoading(true);
            const response = await apiRequest('/api/qna', {
                method: 'POST',
                body: JSON.stringify({
                    page,
                    limit: pagination.itemsPerPage,
                    search_term: searchTerm,
                    sort_column: sortColumn,
                    sort_order: sortDirection,
                    claimed_filter: claimedFilter !== '' ? parseInt(claimedFilter) : null,
                    client_filter: clientFilter !== '' ? parseInt(clientFilter) : null,
                    date_from: dateRange ? moment(dateRange[0].startDate).format('YYYY-MM-DD') : null,
                    date_to: dateRange ? moment(dateRange[0].endDate).format('YYYY-MM-DD') : null,
                }),
            });

            if (response.success) {
                setQuestions(response.data.questions || []);
                setPagination(response.data.pagination);
            } else {
                showToast('Failed to fetch questions', 'error');
            }
        } catch (error) {
            console.error('Error fetching questions:', error);
            showToast('Failed to fetch questions', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // Load questions on mount and when filters change
    useEffect(() => {
        fetchQuestions(1);
    }, [searchTerm, sortColumn, sortDirection, claimedFilter, clientFilter, dateRange]);

    // Handle page change
    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchQuestions(newPage);
        }
    };

    // Handle clearing the date range filter
    const handleClearDateRange = (e: React.MouseEvent) => {
        e.stopPropagation();
        setDateRange(null);
    };

    // Handle opening the date range picker (seed draft from currently applied range)
    const handleToggleDateRangePicker = () => {
        if (!showDateRangePicker) {
            setDateRangeDraft(dateRange || [{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
        }
        setShowDateRangePicker(!showDateRangePicker);
    };

    // Handle applying the draft date range as the active filter
    const handleApplyDateRange = () => {
        setDateRange(dateRangeDraft);
        setShowDateRangePicker(false);
    };

    // Handle cancelling the in-progress date selection
    const handleCancelDateRange = () => {
        setShowDateRangePicker(false);
    };

    // Handle exporting the currently filtered questions as CSV
    const handleExport = async () => {
        try {
            setIsExporting(true);
            await downloadCsvExport('/api/qna/export', {
                search_term: searchTerm,
                claimed_filter: claimedFilter !== '' ? claimedFilter : null,
                client_filter: clientFilter !== '' ? clientFilter : null,
                date_from: dateRange ? moment(dateRange[0].startDate).format('YYYY-MM-DD') : null,
                date_to: dateRange ? moment(dateRange[0].endDate).format('YYYY-MM-DD') : null,
            });
        } catch (error) {
            console.error('Error exporting questions:', error);
            showToast('Failed to export questions', 'error');
        } finally {
            setIsExporting(false);
        }
    };

    // Handle sort
    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortColumn(column);
            setSortDirection('ASC');
        }
    };

    // Handle question click
    const handleQuestionClick = async (question: QuestionData) => {
        setSelectedQuestion(question);
        setIsModalOpen(true);
        setIsLoadingDetails(true);

        try {
            const response = await apiRequest(`/api/qna/${question.question_id}`);
            if (response.success) {
                setSelectedQuestion(response.data);
            }
        } catch (error) {
            console.error('Error fetching question details:', error);
        } finally {
            setIsLoadingDetails(false);
        }
    };

    // Handle modal close
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedQuestion(null);
        setIsEditing(false);
        setNewAnswer('');
    };

    // Handle status toggle
    const handleToggleStatus = async (status: number) => {
        if (!selectedQuestion) return;

        try {
            const newStatus = status === 1 ? 0 : 1;
            const response = await apiRequest(`/api/qna/${selectedQuestion.question_id}/status`, {
                method: 'PUT',
                body: JSON.stringify({ status: newStatus }),
            });

            if (response.success) {
                showToast(`Question ${newStatus === 1 ? 'activated' : 'deactivated'} successfully`, 'success');
                handleCloseModal();
                fetchQuestions(pagination.currentPage);
            } else {
                showToast('Failed to update question status', 'error');
            }
        } catch (error) {
            console.error('Error updating question status:', error);
            showToast('Failed to update question status', 'error');
        }
    };

    // Handle edit question
    const handleEditQuestion = () => {
        if (!selectedQuestion) return;
        setIsEditing(true);
        setEditedTitle(selectedQuestion.title);
        setEditedContent(selectedQuestion.content);
    };

    // Handle save question
    const handleSaveQuestion = async () => {
        if (!selectedQuestion) return;

        try {
            const response = await apiRequest(`/api/qna/${selectedQuestion.question_id}`, {
                method: 'PUT',
                body: JSON.stringify({
                    title: editedTitle,
                    content: editedContent,
                }),
            });

            if (response.success) {
                showToast('Question updated successfully', 'success');
                setIsEditing(false);
                // Reload question details
                const detailsResponse = await apiRequest(`/api/qna/${selectedQuestion.question_id}`);
                if (detailsResponse.success) {
                    setSelectedQuestion(detailsResponse.data);
                }
                fetchQuestions(pagination.currentPage);
            } else {
                showToast('Failed to update question', 'error');
            }
        } catch (error) {
            console.error('Error updating question:', error);
            showToast('Failed to update question', 'error');
        }
    };

    // Handle cancel edit
    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditedTitle('');
        setEditedContent('');
    };

    // Handle submit answer
    const handleSubmitAnswer = async () => {
        if (!selectedQuestion || !newAnswer.trim()) {
            showToast('Please enter an answer', 'error');
            return;
        }

        try {
            setIsSubmittingAnswer(true);
            const response = await apiRequest('/api/qna/answers', {
                method: 'POST',
                body: JSON.stringify({
                    question_id: selectedQuestion.question_id,
                    content: newAnswer,
                }),
            });

            if (response.success) {
                showToast('Answer submitted successfully', 'success');
                setNewAnswer('');
                // Reload question details to show new answer
                const detailsResponse = await apiRequest(`/api/qna/${selectedQuestion.question_id}`);
                if (detailsResponse.success) {
                    setSelectedQuestion(detailsResponse.data);
                }
            } else {
                showToast('Failed to submit answer', 'error');
            }
        } catch (error) {
            console.error('Error submitting answer:', error);
            showToast('Failed to submit answer', 'error');
        } finally {
            setIsSubmittingAnswer(false);
        }
    };

    // Get status badge
    const getStatusBadge = (status: number) => {
        return status === 1 ? (
            <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Active</span>
        ) : (
            <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Inactive</span>
        );
    };

    // Format date
    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const dateStr = date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
        const timeStr = date.toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        }).replace(/am/i, 'AM').replace(/pm/i, 'PM');
        return `${dateStr}, ${timeStr}`;
    };

    // Truncate text
    const truncateText = (text: string, maxLength: number = 80) => {
        if (!text) return '-';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    return (
        <div className="h-full flex flex-col bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-8 py-4">
                <div className="flex items-center gap-4">
                    {/* Left Side - Icon, Title with Badge - 25% */}
                    <div className="flex items-center gap-3" style={{ flex: '0 0 25%' }}>
                        <div className="flex-shrink-0">
                            <div className="p-2 bg-brandColor rounded-lg text-white">
                                <MessageCircleQuestion className="h-5 w-5" />
                            </div>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3">
                                <h1 className="text-lg font-semibold text-gray-900">Q&A Management</h1>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brandColor text-white">
                                    {pagination.totalItems.toLocaleString()}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500">Manage questions and answers</p>
                        </div>
                    </div>

                    {/* Right Side - Search and Filters - 75% */}
                    <div className="flex items-center gap-3 justify-end" style={{ flex: '0 0 75%' }}>
                        {/* Search */}
                        <div className="relative" style={{ width: '250px' }}>
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search questions..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        {/* Claimed Toggle */}
                        <select
                            value={claimedFilter}
                            onChange={(e) => setClaimedFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">All Claimed</option>
                            <option value="1">Claimed</option>
                            <option value="0">Not Claimed</option>
                        </select>

                        {/* Client Toggle */}
                        <select
                            value={clientFilter}
                            onChange={(e) => setClientFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">All Client</option>
                            <option value="1">Client</option>
                            <option value="0">Not Client</option>
                        </select>

                        {/* Created Date Filter */}
                        <div className="relative">
                            <button
                                className="border border-gray-300 rounded-md px-3 py-2 inline-flex items-center gap-2 hover:bg-gray-50"
                                onClick={handleToggleDateRangePicker}
                            >
                                {dateRange ? (
                                    <>
                                        {moment(dateRange[0].startDate).format('MMM D')} - {moment(dateRange[0].endDate).format('MMM D')}
                                        <X className="h-3.5 w-3.5 text-gray-400 hover:text-gray-700" onClick={handleClearDateRange} />
                                    </>
                                ) : (
                                    <>
                                        Created Date
                                        <Filter className="h-3.5 w-3.5" />
                                    </>
                                )}
                            </button>

                            {showDateRangePicker && (
                                <div className="absolute right-0 mt-2 z-[9999] shadow-lg rounded-md bg-white border border-gray-200">
                                    <DateRangePicker
                                        ranges={dateRangeDraft}
                                        onChange={(item) => setDateRangeDraft([item.selection as Range])}
                                        months={1}
                                        direction="horizontal"
                                        editableDateInputs
                                    />
                                    <div className="flex justify-end gap-2 px-3 py-2 border-t border-gray-200">
                                        <Button variant="outline" size="sm" onClick={handleCancelDateRange}>
                                            Cancel
                                        </Button>
                                        <Button variant="primary" size="sm" onClick={handleApplyDateRange}>
                                            Apply
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Export */}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleExport}
                            disabled={isExporting}
                            leftIcon={<Download size={16} />}
                        >
                            {isExporting ? 'Exporting...' : 'Export'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto px-4 mt-4">
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full table-auto border-collapse">
                        <thead className="bg-brandColor text-white sticky top-0 z-10">
                            <tr>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors whitespace-nowrap"
                                    onClick={() => handleSort('slate_id_fk')}
                                >
                                    School ID {sortColumn === 'slate_id_fk' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors whitespace-nowrap"
                                    onClick={() => handleSort('slate_name')}
                                >
                                    School Name {sortColumn === 'slate_name' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors whitespace-nowrap"
                                    onClick={() => handleSort('uPhone')}
                                >
                                    Phone {sortColumn === 'uPhone' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors whitespace-nowrap"
                                    onClick={() => handleSort('title')}
                                >
                                    Question Title {sortColumn === 'title' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors whitespace-nowrap"
                                    onClick={() => handleSort('status')}
                                >
                                    Status {sortColumn === 'status' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors whitespace-nowrap"
                                    onClick={() => handleSort('created_at')}
                                >
                                    Created Date {sortColumn === 'created_at' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors whitespace-nowrap"
                                    onClick={() => handleSort('claimed')}
                                >
                                    Claimed {sortColumn === 'claimed' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors whitespace-nowrap"
                                    onClick={() => handleSort('is_client')}
                                >
                                    Client {sortColumn === 'is_client' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                                        Loading questions...
                                    </td>
                                </tr>
                            ) : questions.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                                        No questions found
                                    </td>
                                </tr>
                            ) : (
                                questions.map((question, index) => (
                                    <tr
                                        key={question.question_id}
                                        className={`hover:bg-blue-50 cursor-pointer ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                                        onClick={() => handleQuestionClick(question)}
                                    >
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            {question.slate_id_fk}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-800 font-medium">
                                            {question.slate_name || '-'}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            {question.user_phone || '-'}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            <div className="flex items-center gap-2">
                                                {truncateText(question.title, 80)}
                                                {question.answer_count > 0 && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">
                                                        <MessageSquare size={12} className="mr-1" />
                                                        {question.answer_count}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 text-sm">
                                            {getStatusBadge(question.status)}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600 whitespace-nowrap">
                                            {formatDate(question.created_at)}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-center">
                                            {question.claimed === 1 ? (
                                                <CheckCircle size={18} className="text-green-600 inline" />
                                            ) : (
                                                <XCircle size={18} className="text-gray-400 inline" />
                                            )}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-center">
                                            {question.is_client === 1 ? (
                                                <CheckCircle size={18} className="text-green-600 inline" />
                                            ) : (
                                                <XCircle size={18} className="text-gray-400 inline" />
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                <div className="text-sm text-gray-600">
                    Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
                    {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of{' '}
                    {pagination.totalItems} results
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(1)}
                        disabled={pagination.currentPage === 1}
                    >
                        <ChevronsLeft size={16} />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={pagination.currentPage === 1}
                    >
                        <ChevronLeft size={16} />
                    </Button>
                    <span className="text-sm text-gray-600">
                        Page {pagination.currentPage} of {pagination.totalPages}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={pagination.currentPage === pagination.totalPages}
                    >
                        <ChevronRight size={16} />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.totalPages)}
                        disabled={pagination.currentPage === pagination.totalPages}
                    >
                        <ChevronsRight size={16} />
                    </Button>
                </div>
            </div>

            {/* Question Detail Modal */}
            <SettingsSlideModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title="Question Details"
                subtitle={selectedQuestion ? `Q #${selectedQuestion.question_id}` : ''}
                icon={<div className="text-brandColor"><MessageCircleQuestion size={24} /></div>}
                width="700px"
            >
                {selectedQuestion && (
                    <>
                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            {/* School Info */}
                            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                                <h3 className="font-semibold text-gray-900">{selectedQuestion.slate_name}</h3>
                                <div className="text-sm text-gray-600">School ID: {selectedQuestion.slate_id_fk}</div>
                                <div className="flex gap-4 mt-2">
                                    <div className="flex items-center gap-1">
                                        <span className="text-xs text-gray-500">Claimed:</span>
                                        {selectedQuestion.claimed === 1 ? (
                                            <CheckCircle size={16} className="text-green-600" />
                                        ) : (
                                            <XCircle size={16} className="text-gray-400" />
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <span className="text-xs text-gray-500">Client:</span>
                                        {selectedQuestion.is_client === 1 ? (
                                            <CheckCircle size={16} className="text-green-600" />
                                        ) : (
                                            <XCircle size={16} className="text-gray-400" />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Question Info */}
                            <div className="space-y-4">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        {isEditing ? (
                                            <div>
                                                <label className="text-xs text-gray-500 uppercase">Question Title</label>
                                                <input
                                                    type="text"
                                                    value={editedTitle}
                                                    onChange={(e) => setEditedTitle(e.target.value)}
                                                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                />
                                            </div>
                                        ) : (
                                            <div>
                                                <label className="text-xs text-gray-500 uppercase">Question Title</label>
                                                <p className="text-base font-semibold text-gray-900 mt-1">{selectedQuestion.title}</p>
                                            </div>
                                        )}
                                    </div>
                                    {!isEditing && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleEditQuestion}
                                            className="ml-2 border-0"
                                        >
                                            <Edit2 size={16} />
                                        </Button>
                                    )}
                                </div>
                                <div>
                                    {isEditing ? (
                                        <div>
                                            <label className="text-xs text-gray-500 uppercase">Question Content</label>
                                            <textarea
                                                value={editedContent}
                                                onChange={(e) => setEditedContent(e.target.value)}
                                                rows={6}
                                                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                            />
                                            <div className="flex gap-2 mt-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={handleCancelEdit}
                                                    className="border-0"
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={handleSaveQuestion}
                                                    className="bg-green-600 hover:bg-green-700 border-0"
                                                >
                                                    Save Changes
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <label className="text-xs text-gray-500 uppercase">Question Content</label>
                                            <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{selectedQuestion.content}</p>
                                        </div>
                                    )}
                                </div>
                                {!isEditing && (
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-xs text-gray-500 uppercase">Status</label>
                                            <div className="mt-1">{getStatusBadge(selectedQuestion.status)}</div>
                                        </div>
                                        <div>
                                            <label className="text-xs text-gray-500 uppercase">Created Date</label>
                                            <p className="text-sm font-medium text-gray-900">{formatDate(selectedQuestion.created_at)}</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* User Info */}
                            <div className="border-t pt-4">
                                <h4 className="text-sm font-semibold text-gray-900 mb-3">Asked By</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs text-gray-500 uppercase">Name</label>
                                        <p className="text-sm font-medium text-gray-900">{selectedQuestion.user_name || '-'}</p>
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 uppercase">Phone</label>
                                        <p className="text-sm font-medium text-gray-900">{selectedQuestion.user_phone || '-'}</p>
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 uppercase">Email</label>
                                        <p className="text-sm font-medium text-gray-900">{selectedQuestion.user_email || '-'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Answers Section */}
                            {isLoadingDetails ? (
                                <div className="border-t pt-4">
                                    <p className="text-sm text-gray-500">Loading answers...</p>
                                </div>
                            ) : selectedQuestion.answers && selectedQuestion.answers.length > 0 ? (
                                <div className="border-t pt-4">
                                    <h4 className="text-sm font-semibold text-gray-900 mb-3">
                                        Answers ({selectedQuestion.answers.length})
                                    </h4>
                                    <div className="space-y-4">
                                        {selectedQuestion.answers.map((answer) => (
                                            <div key={answer.answer_id} className="bg-blue-50 rounded-lg p-4 space-y-2">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <p className="text-sm text-gray-800">{answer.content}</p>
                                                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-600">
                                                            <span>{answer.user_name || 'Anonymous'}</span>
                                                            <span>{formatDate(answer.created_at)}</span>
                                                        </div>
                                                    </div>
                                                    {getStatusBadge(answer.status)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="border-t pt-4">
                                    <p className="text-sm text-gray-500">No answers yet</p>
                                </div>
                            )}

                        </div>

                        {/* Fixed Footer with Answer Input and Action Buttons */}
                        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 space-y-4">
                            {/* Add Answer Section */}
                            {!isEditing && (
                                <div>
                                    <label className="text-sm font-semibold text-gray-900 mb-2 block">Add Answer</label>
                                    <textarea
                                        value={newAnswer}
                                        onChange={(e) => setNewAnswer(e.target.value)}
                                        placeholder="Type your answer here..."
                                        rows={3}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                    />
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex gap-3 justify-between">
                                <Button
                                    variant="outline"
                                    onClick={() => handleToggleStatus(selectedQuestion.status)}
                                    className={selectedQuestion.status === 1 ? 'border-red-300 text-red-600 hover:bg-red-50' : 'border-green-300 text-green-600 hover:bg-green-50'}
                                >
                                    {selectedQuestion.status === 1 ? 'Deactivate' : 'Activate'}
                                </Button>
                                <div className="flex gap-3">
                                    <Button
                                        variant="outline"
                                        onClick={handleCloseModal}
                                    >
                                        Cancel
                                    </Button>
                                    {!isEditing && (
                                        <Button
                                            variant="primary"
                                            onClick={handleSubmitAnswer}
                                            disabled={isSubmittingAnswer || !newAnswer.trim()}
                                            leftIcon={<Send size={16} />}
                                        >
                                            {isSubmittingAnswer ? 'Submitting...' : 'Submit Answer'}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </SettingsSlideModal>
        </div>
    );
};

export default QAAdmin;
